import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { cldImage } from "@/lib/cldImage";

const HERO = cldImage("https://images.unsplash.com/photo-1571877227200-a0d98ea607e9", { w: 1400 });

/**
 * Customer login/sign-up page. Two paths — "Continue with Google" (Emergent
 * OAuth) and a self-contained email/password form with a Sign in / Sign up
 * toggle. All roads set the same `session_token` httpOnly cookie so the rest
 * of the app treats both provider types identically.
 */
export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/menu";
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      // Owners land in the dashboard; everyone else goes wherever they were headed.
      if (user.is_admin && next.startsWith("/admin")) navigate("/admin/dashboard", { replace: true });
      else navigate(next.startsWith("/admin") ? "/" : next, { replace: true });
    }
  }, [user, navigate, next]);

  const signInGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !password) { setErr("Email and password required."); return; }
    if (mode === "signup" && password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      const endpoint = mode === "signup" ? "/auth/register" : "/auth/login-email";
      const body = mode === "signup" ? { email, password, name } : { email, password };
      const { data } = await api.post(endpoint, body);
      setUser(data);
      toast.success(mode === "signup" ? "Welcome to the boutique." : "Signed in.");
      // `user` change triggers the redirect useEffect above.
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(" ") : "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="login-page" className="min-h-screen bg-espresso text-ivory">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen grid md:grid-cols-12 gap-10 items-center py-16">
        <div className="md:col-span-6">
          <Link to="/" className="label-xs text-ivory/60 link-underline">← Back home</Link>
          <div className="mt-8 label-xs text-gold/80">Welcome</div>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight text-ivory font-normal">
            {mode === "signup" ? "Create an account" : "Welcome back"}
            <br/>
            <span className="italic text-ivory/60 font-light">
              {mode === "signup" ? "and begin a spoon." : "your basket is waiting."}
            </span>
          </h1>
          <p className="mt-6 text-ivory/70 max-w-md leading-relaxed text-sm">
            {mode === "signup"
              ? <>One account for our boutique — order faster, save your address, and unlock a <span className="text-gold">₹50 welcome offer</span> on your first order over ₹199.</>
              : "Sign in to see your order history and pre-fill your delivery details."}
          </p>

          {loading ? (
            <div className="mt-10 label-xs text-ivory/50">Checking session…</div>
          ) : (
            <>
              <button
                onClick={signInGoogle}
                data-testid="customer-signin-btn"
                className="mt-10 w-full max-w-md inline-flex items-center justify-center gap-3 px-8 py-4 bg-ivory text-espresso rounded-full text-xs uppercase tracking-[0.24em] hover:bg-gold transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
                Continue with Google
              </button>

              <div className="mt-6 flex items-center gap-4 max-w-md">
                <div className="flex-1 h-px bg-ivory/20"></div>
                <div className="label-xs text-ivory/50">or</div>
                <div className="flex-1 h-px bg-ivory/20"></div>
              </div>

              <form onSubmit={submitEmail} className="mt-6 max-w-md space-y-3" data-testid="email-auth-form">
                {mode === "signup" && (
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="signup-name"
                    className="w-full bg-transparent border border-ivory/30 px-4 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory rounded-sm"
                  />
                )}
                <input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="w-full bg-transparent border border-ivory/30 px-4 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory rounded-sm"
                />
                <input
                  type="password"
                  placeholder={mode === "signup" ? "Choose a password (6+ chars)" : "Password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="w-full bg-transparent border border-ivory/30 px-4 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory rounded-sm"
                />
                {err && <p className="text-xs text-red-300" data-testid="auth-error">{err}</p>}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="email-submit-btn"
                  className="w-full px-8 py-4 bg-gold text-espresso rounded-full text-xs uppercase tracking-[0.24em] hover:bg-gold-hover disabled:opacity-40 transition-colors"
                >
                  {busy ? "…" : (mode === "signup" ? "Create account" : "Sign in")}
                </button>
              </form>

              <button
                type="button"
                data-testid="auth-mode-toggle"
                onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setErr(""); }}
                className="mt-4 label-xs text-ivory/60 hover:text-ivory link-underline"
              >
                {mode === "signup" ? "Already have an account? Sign in →" : "New here? Sign up with email →"}
              </button>
              {mode === "signin" && (
                <div className="mt-3">
                  <Link to="/forgot-password" data-testid="forgot-link" className="label-xs text-ivory/60 hover:text-ivory link-underline">
                    Forgot your password?
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
        <div className="md:col-span-6 hidden md:block">
          <div className="aspect-[4/5] max-h-[640px] overflow-hidden bg-espresso/80">
            <img src={HERO} alt="Tiramisu, layered by hand" className="w-full h-full object-cover opacity-90" />
          </div>
        </div>
      </div>
    </main>
  );
}
